'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip@1.1.8';

import { cn } from './utils';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 6,
  showArrow = false,
  showAccent = true,
  accentSide = 'left',
  style,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  /** Render the (white) pointer arrow. Off by default — the redesigned card is detached. */
  showArrow?: boolean;
  /** Render the purple→indigo accent bar (LIL-409). */
  showAccent?: boolean;
  /** Which edge the accent bar sits on. 'top' suits dropping tooltips (e.g. TopBar). */
  accentSide?: 'left' | 'top';
}) {
  const accentOnTop = accentSide === 'top';
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        // maxWidth and color are inline styles on purpose: arbitrary `max-w-[Npx]`
        // utilities aren't generated in this Tailwind v4 setup (the card would never
        // wrap), and inline color guarantees the slate text (LIL-409).
        style={{ maxWidth: 200, color: '#475569', backgroundColor: '#FAF7FF', ...style }}
        className={cn(
          'relative overflow-hidden border border-purple-100 shadow-[0_12px_32px_-12px_rgba(17,24,39,0.25)] animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-2xl text-[13px] font-medium leading-snug',
          accentOnTop ? 'px-3.5 pb-2.5 pt-3' : 'py-2.5 pl-4 pr-3.5',
          className
        )}
        {...props}
      >
        {showAccent && (
          <span
            aria-hidden
            className="absolute"
            // Box + gradient are fully inline: sizing utilities (inset-x/h-1.5)
            // aren't reliably generated in this Tailwind v4 setup, which collapsed
            // the top bar to zero size (LIL-409).
            style={
              accentOnTop
                ? {
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 6,
                    backgroundImage:
                      'linear-gradient(to right, #D42BD9 0%, #9333EA 50%, #4F46E5 100%)',
                  }
                : {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: 6,
                    backgroundImage:
                      'linear-gradient(to bottom, #D42BD9 0%, #9333EA 50%, #4F46E5 100%)',
                  }
            }
          />
        )}
        {children}
        {showArrow && (
          <TooltipPrimitive.Arrow
            width={12}
            height={6}
            className="fill-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.06)]"
          />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
